import "./SaveViewMenu.css";

import {
  Button,
  ButtonIcon,
  ButtonSelect,
  Icon,
  type IconKind,
  Menu,
  MenuItem,
  type MenuOption,
} from "@saviynt/design-system";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useUpdateSaveFilterView } from "../../../../hooks/usePolicySaveFilterView";
import { parseError } from "../../../PoliciesFindingsList/utils";
import {
  errorMessages,
  ALL_POLICIES,
} from "../../../../constants/filterSaveView.constants";
import {
  Preference,
  SavedFilterView,
} from "@repo/api/schemas/policiesSaveFilterView.schema";
import DeleteViewModal from "../DeleteViewModal/DeleteViewModal";
import RenameViewModal from "../RenameViewModal/RenameViewModal";
import {
  usePoliciesFilter,
  type PoliciesFilterValues,
} from "../../../../context/PoliciesFilterContext";
import { WORKFORCE_TAB } from "../../../../constants/policies.constants";

interface CustomMenuOption extends MenuOption {
  isDefault?: boolean;
  viewId: number;
  preference: Preference | null;
}

interface SaveViewMenuProps {
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  saveViewList: SavedFilterView[];
}

export default function SaveViewMenu({
  setIsModalOpen,
  saveViewList,
}: SaveViewMenuProps) {
  const intl = useIntl();
  const { filterValues, setFilterValues } = usePoliciesFilter();
  const menuActionViewDataRef = useRef({ name: "", id: -1 });
  const defaultViewName = useMemo(() => {
    const defaultView = saveViewList.find((view) => view.is_default);
    return defaultView ? `${defaultView.view_name} (default)` : "";
  }, [saveViewList]);
  const [selectedView, setSelectedView] = useState(defaultViewName);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { mutate } = useUpdateSaveFilterView({
    onError: (error: unknown) => {
      const { message, code = "" } = parseError(error);
      const messageFromMap =
        code in errorMessages
          ? intl.formatMessage(
              errorMessages[code as keyof typeof errorMessages],
            )
          : message;
      console.error("===Make it Default Error: ====", messageFromMap);
    },
  });
  // Set of actions applicable to each Save View menu item
  const menuActions = useMemo(
    () => [
      {
        label: intl.formatMessage({
          description:
            "Menu option to set a saved NHI inventory view as the default view that loads when accessing the dashboard",
          defaultMessage: "Make Default",
          id: "xBts8G",
        }),
        value: "make_default",
        icon: "StarOutline",
      },
      {
        label: intl.formatMessage({
          id: "W+1J+E",
          defaultMessage: "Rename",
          description: "Menu option to rename a saved NHI inventory view",
        }),
        value: "rename",
        icon: "Edit",
      },
      {
        label: intl.formatMessage({
          id: "u6HkAn",
          defaultMessage: "Delete",
          description: "Menu option to delete a saved NHI inventory view",
        }),
        value: "delete",
        icon: "Delete",
      },
    ],
    [intl],
  );

  const applyViewPreferences = useCallback(
    (preference: Preference | null) => {
      const savedFilters =
        (preference?.selectedFilters as unknown as PoliciesFilterValues["selectedFilters"]) ??
        {};
      const fallbackCategory = filterValues.selectedFilters.category ?? [
        { value: WORKFORCE_TAB, label: WORKFORCE_TAB, isChecked: true },
      ];
      setFilterValues({
        searchValue: preference?.searchValue ?? "",
        selectedFilters: {
          ...savedFilters,
          category: savedFilters.category ?? fallbackCategory,
        },
      });
    },
    [setFilterValues, filterValues.selectedFilters.category],
  );

  const initialOptions = useMemo(() => {
    return saveViewList.map((view) => {
      const { view_name, is_default, id, preference = null } = view;

      return {
        label: is_default ? `${view_name} (default)` : view_name,
        value: view_name,
        isChecked: is_default,
        isDefault: is_default,
        viewId: id,
        preference,
        icon: is_default ? "StarOutline" : undefined,
      };
    });
  }, [saveViewList]);

  const handleMenuChange = useCallback(
    (selectedOption: CustomMenuOption) => {
      if (selectedOption.value === "save_new_view") {
        setIsModalOpen(true);
        return;
      }

      setSelectedView(selectedOption.label);

      setOptions((currentOptions) =>
        currentOptions.map((option) => ({
          ...option,
          isChecked: option.value === selectedOption.value,
        })),
      );
      applyViewPreferences(selectedOption.preference);
      setIsMenuOpen(false);
    },
    [applyViewPreferences, setIsModalOpen],
  );

  const hasAppliedInitialRef = useRef(false);
  const [options, setOptions] = useState(initialOptions);

  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  useEffect(() => {
    setSelectedView(defaultViewName);
  }, [defaultViewName]);

  // Apply the default view's preferences once when the list first loads
  useEffect(() => {
    if (hasAppliedInitialRef.current || saveViewList.length === 0) return;
    hasAppliedInitialRef.current = true;
    const defaultView = saveViewList.find((view) => view.is_default);
    applyViewPreferences(defaultView?.preference ?? null);
  }, [saveViewList, applyViewPreferences]);

  const handleMakeDefault = useCallback(
    (viewName: string, viewId: number) => {
      const selectedView = saveViewList.find((view) => view.id === viewId);

      const onSuccess = () => {
        // Apply filters of the newly marked default view or clear if null
        applyViewPreferences(selectedView?.preference || null);
      };

      mutate(
        {
          view_name: viewName,
          id: viewId,
          is_default: true,
        },
        { onSuccess },
      );
    },
    [mutate, saveViewList, applyViewPreferences],
  );

  const handleMenuActionChange = useCallback(
    (selectedOption: MenuOption, viewName: string, viewId: number) => {
      menuActionViewDataRef.current = { name: viewName, id: viewId };

      switch (selectedOption.value) {
        case "rename":
          setIsRenameModalOpen(true);
          break;
        case "delete":
          setIsDeleteModalOpen(true);
          break;
        case "make_default":
          handleMakeDefault(viewName, viewId);
          break;
      }
      setIsMenuOpen(false);
    },
    [handleMakeDefault],
  );

  const getMenuActions = useCallback(
    (option: CustomMenuOption) => {
      const filteredActions =
        option.value === ALL_POLICIES
          ? menuActions.filter((menu) => menu.value === "make_default")
          : menuActions;

      return filteredActions.map((menu) =>
        option.isDefault && menu.value === "make_default"
          ? { ...menu, isDisabled: true }
          : menu,
      );
    },
    [menuActions],
  );

  const getCustomMenuItems = (menuOptions: CustomMenuOption[]) => (
    <>
      {menuOptions.map((option) => (
        <MenuItem
          key={option.value}
          option={option}
          onClick={() => {
            setIsMenuOpen(false);
            handleMenuChange(option);
          }}
          tabIndex={0}
          prefixIcon={
            option.icon ? <Icon kind={option.icon as IconKind} /> : undefined
          }
          suffixIcon={
            <Menu
              options={getMenuActions(option)}
              onChange={(selectedOption: MenuOption) =>
                handleMenuActionChange(
                  selectedOption,
                  option.label,
                  option.viewId,
                )
              }
              trigger={
                <ButtonIcon
                  size="small"
                  kind="ghost"
                  icon={<Icon kind="MoreVertical" />}
                  ariaLabel={option.label}
                />
              }
              classNames={{
                dropdownClassName: "save-view-menu-actions-dropdown",
              }}
            />
          }
        />
      ))}
    </>
  );

  return (
    <>
      <Menu
        isMenuExpanded={isMenuOpen}
        setIsExpanded={setIsMenuOpen}
        CustomMenuItems={getCustomMenuItems(options)}
        isOutsideClickEnabled={true}
        trigger={
          <ButtonSelect
            kind="rectangle"
            label={selectedView}
            size="small"
            className="filter-btn-height-3"
          />
        }
        footerComp={
          <Button
            type="button"
            kind="ghost"
            size="medium"
            leftIcon={
              <Icon kind="Saved" color="--color-foreground-neutral-disabled" />
            }
            onClick={() => {
              setIsMenuOpen(false);
              setIsModalOpen(true);
            }}
          >
            {intl.formatMessage({
              description:
                "Button text for creating a new saved view with current NHI inventory filters and preferences",
              defaultMessage: "Save New View",
              id: "pCz6m7",
            })}
          </Button>
        }
        classNames={{
          dropdownClassName: "save-view-menu-dropdown",
        }}
      />

      <RenameViewModal
        isOpen={isRenameModalOpen}
        setIsOpen={setIsRenameModalOpen}
        viewData={menuActionViewDataRef.current}
      />
      <DeleteViewModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        viewData={menuActionViewDataRef.current}
      />
    </>
  );
}
